import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FestivalBoardPill — header chip that appears ONLY when a Festival
 * Board is active (e.g., 🎆 4th of July board July 1–5 PT).
 * Pulses when free rolls are available. Links to /festival.
 */
export default function FestivalBoardPill() {
  const { isAuthed } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    async function load() {
      try {
        const { data } = await axios.get(`${API}/festival/state`);
        if (!cancelled) setData(data);
      } catch (_e) { /* ignore */ }
    }
    load();
    const iv = setInterval(load, 90_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isAuthed]);

  if (!isAuthed || !data?.active || !data?.board) return null;

  const board = data.board;
  const state = data.state || {};
  const freeRolls = state.free_rolls_remaining ?? 0;
  const stamps = (state.stamps_collected || []).length;
  const stampsNeed = board.stamps?.stamps_for_boss ?? 3;
  const bossReady = stamps >= stampsNeed && !state.boss_defeated;
  const grandReady = state.boss_defeated && !state.grand_prize_claimed;
  const pulse = bossReady || grandReady || freeRolls > 0;

  return (
    <Link
      to="/arena"
      data-testid="festival-board-pill"
      className={`flex items-center gap-1.5 px-2.5 py-1 border transition-all ${
        pulse
          ? "border-amber-400 bg-amber-400/10 animate-pulse hover:bg-amber-400/20"
          : "border-white/15 bg-black/40 hover:border-amber-400/50"
      }`}
      style={{ borderRadius: 999 }}
      title={
        grandReady ? "Grand Prize ready to claim!" :
        bossReady ? `Festival Boss unlocked — fight ${board.boss?.name || "boss"}!` :
        freeRolls > 0 ? `${freeRolls} free roll${freeRolls > 1 ? "s" : ""} ready` :
        `${board.name}`
      }
    >
      <span className="text-base leading-none">{board.icon}</span>
      <span className="text-[11px] font-bold tabular-nums text-white">
        {grandReady ? (
          <span className="text-amber-300">CLAIM!</span>
        ) : bossReady ? (
          <span className="text-rose-300">BOSS!</span>
        ) : (
          <>
            <span className={freeRolls > 0 ? "text-amber-300" : ""}>{freeRolls}</span>
            <span className="text-white/40">/{board.rolls?.free_per_day ?? 3}</span>
          </>
        )}
      </span>
    </Link>
  );
}
